import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import _ from 'lodash';

import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';

import ReloadViewModule from '../../../services/ReloadViewModule';
import SaveCustomerOrderDevices from '../../../services/SaveCustomerOrderDevices';
import UpdateOrderLineItems from '../../../services/UpdateOrderLineItems';
import VB0DeviceSectionView from '../../../views/VB0DeviceSectionView';

import tmplCONUSFormView from './VB0FormView.hbs';

const VB0ConusFormView = View.extend({
  template: tmplCONUSFormView,

  ui: {
    vb0DescriptionSection: '.vb0-description-section',
    vb0NIPRSection: '.vb0-nipr-section',
    vb0SIPRSection: '.vb0-sipr-section',
    vb0TonerAndPaperSection: '.vb0-toner-paper-section',
    devicesAlert: '.no-devices-error'
  },

  regions: {
    vb0DescriptionSection: '@ui.vb0DescriptionSection',
    vb0NIPRSection: '@ui.vb0NIPRSection',
    vb0SIPRSection: '@ui.vb0SIPRSection',
    vb0TonerAndPaperSection: '@ui.vb0TonerAndPaperSection'
  },

  className: 'vb0-form-view',

  onRender() {
    this.vb0DeviceSectionView = new VB0DeviceSectionView();
    const orderType = this.model.get('orderDetails').orderTypeId;
    const isAdminEdit = this.model.get('orderDetails').isAdminEdit;

    const { Printer, Scanner, Optional } = _.groupBy(this.model.get('lineItems'), 'panel');
    const niprDevices = [];
    const siprDevices = [];
    const devices = Printer.concat(Scanner);
    // const niprDevices = _.chain(devices)
    //   .filter(devices, (item) => {
    //     return item.isNIPR === '1';
    //   })
    //   .groupBy('volumeBand')
    //   .value();
    _.each(devices, (device) => {
      if (device.isNIPR === '1') {
        niprDevices.push(device);
      } else siprDevices.push(device);
    });

    this.showChildView(
      'vb0DescriptionSection',
      new VB0DeviceSectionView({
        model: new Backbone.Model({
          title: 'Device Information',
          type: 'description',
          contract: orderType === '16' ? 'VB0 Conus' : 'VB0 OConus',
          id: 'Conus-Description',
          deviceMatrix: this.model.get('deviceMatrix'),
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'vb0NIPRSection',
      new VB0DeviceSectionView({
        model: new Backbone.Model({
          lineItems: niprDevices,
          title: 'NIPR Devices',
          id: 'nipr',
          orderType,
          type: 'devices',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'vb0SIPRSection',
      new VB0DeviceSectionView({
        model: new Backbone.Model({
          lineItems: siprDevices,
          title: 'SIPR Devices',
          id: 'sipr',
          orderType,
          type: 'devices',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'vb0TonerAndPaperSection',
      new VB0DeviceSectionView({
        model: new Backbone.Model({
          lineItems: Optional,
          title: 'Toner Cartridges/Paper Trays for MFDs and Printers',
          id: 'tonerPaperTray',
          orderType,
          type: 'optionalItems',
          isAdminEdit
        })
      })
    );
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occurred while saving devices for this order. Try again or contact an administrator if your issue persists.'
    });
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Save Success',
      message: 'This order has been saved successfully.',
      hideAfter: 5
    });
  },

  showNoChangedDevicesError() {
    showErrorMessage({
      errorCode: 'noChangedDevicesError',
      errorTitle: 'No Devices Updated Error',
      errorMessage:
        'No devices were updated. Please update devices in order to save changes.'
    });
  },

  async validate() {
    const [niprColl, siprColl, optionalColl] = await this.getDeviceData();

    let isValid = true;
    if (
      !niprColl?.totalPrinterQuantity &&
      !niprColl?.totalScannerQuantity &&
      !siprColl?.totalPrinterQuantity &&
      !siprColl?.totalScannerQuantity &&
      !optionalColl?.totalTonerPaperQuantity
    ) {
      isValid = false;
      this.ui.devicesAlert.show();
    }
    isValid ? this.trigger('no:errors') : this.trigger('has:errors');
    return isValid;
  },

  async getDeviceData() {
    return await Promise.all([
      this.getChildView('vb0NIPRSection').getDeviceData(),
      this.getChildView('vb0SIPRSection').getDeviceData(),
      this.getChildView('vb0TonerAndPaperSection').getDeviceData()
    ]);
  },

  getLineItemData(lineItemData){
    const lineItems = this.model.get('lineItems');
    const changedItems = [];

    _.each(lineItems, (item) => {
      const changedPrice = lineItemData[`${item.id}-price`];
      const changedDlaCost = lineItemData[`${item.id}-dlaCost`];
      const changedWarrantyYear = lineItemData[`${item.id}-warrantyYear`];
      const changedPpm = lineItemData[`${item.id}-ppm`];
      const changedDesc = lineItemData[`${item.id}-description`];
      const changedModel = lineItemData[`${item.id}-model`];
      const changedCompatibleDevice = lineItemData[`${item.id}-compatibleDevice`];

      const isPriceChanged = (item.price !== changedPrice) && item.price;
      const isDlaCostChanged = (item.dlaCost !== changedDlaCost) && !!item.dlaCost;
      const isWarrantyYearChanged = (item.warrantyYear !== changedWarrantyYear) && item.warrantyYear;
      const isPpmChanged = (item.speedPPM !== changedPpm) && item.speedPPM;
      const isDescChanged = (item.description !== changedDesc) && item.description;
      const isModelChanged = (item.model !== changedModel) && item.model;
      const isCompatibleDeviceChanged = (item.compatibleDevice !== changedCompatibleDevice) && item.compatibleDevice;

      const changedItem = {};
      if (isPriceChanged) changedItem.price = (parseFloat(changedPrice)).toFixed(2);
      if (isDlaCostChanged) changedItem.dlaCost = changedDlaCost;
      if (isWarrantyYearChanged) changedItem.warrantyYear = changedWarrantyYear;
      if (isPpmChanged) changedItem.speedPPM = changedPpm;
      if (isDescChanged) changedItem.description = changedDesc;
      if (isModelChanged) changedItem.model = changedModel;
      if (isCompatibleDeviceChanged) changedItem.compatibleDevice = changedCompatibleDevice;
      if (!_.isEmpty(changedItem)) {
        changedItem.id = item.id;
        changedItems.push(changedItem);
      }
    })

    return changedItems;
  },

  async onSave(lineItemData) {
    this.ui.devicesAlert.hide();
    this.trigger('no:errors');
    const isAdminEdit = this.model.get('orderDetails').isAdminEdit
    let formData;

    try {
      if(isAdminEdit && lineItemData){
        formData = await this.saveAdminEdit(lineItemData);
        if (formData.changedItems.length > 0) {
          await UpdateOrderLineItems(formData);
          this.showSaveSuccess();
        } else {
          this.showNoChangedDevicesError();
        }      
      } else {
        formData = await this.saveOrder();
        await Promise.all([SaveCustomerOrderDevices(formData), ReloadViewModule.reload()]);
        this.showSaveSuccess();
      }
    } catch (err) {
      this.showSaveError();
      throw new Error(err);
    }
  },

  async saveOrder() {
    const [niprColl, siprColl, optionalColl] = await this.getDeviceData();
    const devicesFormData = niprColl.changedItems.concat(siprColl.changedItems, optionalColl.changedItems);

    const totalPrinterPrice = niprColl?.totalPrinterPrice + siprColl.totalPrinterPrice || 0;
    const totalPrinterQuantity = niprColl?.totalPrinterQuantity + siprColl?.totalPrinterQuantity || 0;

    const totalScannerPrice = niprColl?.totalScannerPrice + siprColl?.totalScannerPrice || 0;
    const totalScannerQuantity = niprColl?.totalScannerQuantity + siprColl?.totalScannerQuantity || 0;

    const totalTonerPaperPrice = optionalColl?.totalTonerPaperPrice || 0;
    const totalTonerPaperQuantity = optionalColl?.totalTonerPaperQuantity || 0;

    const totalOrderPrice = totalPrinterPrice + totalScannerPrice + totalTonerPaperPrice;
    const totalOrderQuantity = totalPrinterQuantity + totalScannerQuantity + totalTonerPaperQuantity;

    const orderFormData = {
      totalOrderPrice,
      totalOrderQuantity,
      totalPrinterPrice,
      totalPrinterQuantity,
      totalScannerPrice,
      totalScannerQuantity,
      totalTonerPaperPrice,
      totalTonerPaperQuantity
    };
    const formData = {
      devicesFormData,
      orderFormData,
      orderId: this.model.get('orderDetails').orderId,
      orderTypeId: this.model.get('orderDetails').orderTypeId,
      itPocExists: true
    };

    return formData;
  },

  saveAdminEdit(lineItemData) {
    const changedItems = this.getLineItemData(lineItemData);
    const formData = {
      orderFormTypeID: this.model.get('orderDetails').orderTypeId,
      changedItems
    };

    return formData;
  }
});

export default VB0ConusFormView;
